import apiServiceHandler from "./APIServiceHandler";
export const BASE_CABIN_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// API URLs
const GetCabinsURL = `${BASE_CABIN_URL}/cabins`;
const GetBookingsURL = `${BASE_CABIN_URL}/bookings`;
const GetAvailabilityURL = `${BASE_CABIN_URL}/availability`;
const CreateBookingURL = `${BASE_CABIN_URL}/book`;
const CreateBulkBookingURL = `${BASE_CABIN_URL}/bulk-book`;
const CancelBookingURL = `${BASE_CABIN_URL}/cancel`;
const CancelBulkBookingURL = `${BASE_CABIN_URL}/cancel-bulk`;
const UpdateBookingURL = `${BASE_CABIN_URL}/update`;
const GetStatsURL = `${BASE_CABIN_URL}/stats`;

// Interfaces
export interface Cabin {
  id: string;
  name: string;
  color: string;
}

export interface Booking {
  _id: string;
  username: string;
  bookedBy: string;
  cabinId: string;
  cabinName: string;
  date: string;
  timeSlot: string;
  title: string;
  description: string;
  bookingType: 'single' | 'bulk';
  bulkBookingId?: string;
  status: 'active' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

export interface TimeSlotBooking {
  date: string;
  timeSlot: string;
}

// Request Payloads
export interface GetBookingsPayload {
  username: string;
  startDate?: string;
  endDate?: string;
}

export interface GetAvailabilityPayload {
  startDate: string;
  endDate: string;
}

export interface CreateBookingPayload {
  username: string;
  cabinId: string;
  date: string;
  timeSlot: string;
  title?: string;
  description?: string;
}

export interface CreateBulkBookingPayload {
  username: string;
  cabinId: string;
  timeSlots: TimeSlotBooking[];
  title?: string;
  description?: string;
}

export interface CancelBookingPayload {
  username: string;
}

export interface CancelBulkBookingPayload {
  username: string;
}

export interface UpdateBookingPayload {
  username: string;
  title?: string;
  description?: string;
}

export interface GetStatsPayload {
  username: string;
  startDate?: string;
  endDate?: string;
}

// Response Interfaces
export interface APIResponse<T> {
  data: {
    message: string;
    [key: string]: any;
  } & T;
  status: 'success' | 'fail' | 'error';
  statusCode: number;
}

export interface GetCabinsResponse {
  cabins: Cabin[];
}

export interface GetBookingsResponse {
  bookings: Booking[];
  count: number;
}

export interface GetAvailabilityResponse {
  bookings: Booking[];
  cabins: Cabin[];
}

export interface CreateBookingResponse {
  booking: Booking;
}

export interface CreateBulkBookingResponse {
  bookings: Booking[];
  bulkBookingId: string;
}

export interface CancelBookingResponse {
  booking: Booking;
}

export interface CancelBulkBookingResponse {
  cancelledCount: number;
}

export interface UpdateBookingResponse {
  booking: Booking;
}

export interface BookingStats {
  totalBookings: number;
  singleBookings: number;
  bulkBookings: number;
  mostUsedCabin: {
    cabinId: string;
    count: number;
  } | null;
}

export interface GetStatsResponse {
  stats: BookingStats;
}

// API Functions
export const GetCabins = async (): Promise<APIResponse<GetCabinsResponse> | undefined> => {
  try {
    const response = await apiServiceHandler.get<APIResponse<GetCabinsResponse>>(GetCabinsURL);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('GetCabins error:', error);
    throw error;
  }
};

export const GetBookings = async (payload: GetBookingsPayload): Promise<APIResponse<GetBookingsResponse> | undefined> => {
  try {
    const url = `${GetBookingsURL}/${payload.username}`;
    const params: any = {};
    if (payload.startDate) params.startDate = payload.startDate;
    if (payload.endDate) params.endDate = payload.endDate;

    const response = await apiServiceHandler.get<APIResponse<GetBookingsResponse>>(url, params);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('GetBookings error:', error);
    throw error;
  }
};

export const GetAvailability = async (payload: GetAvailabilityPayload): Promise<APIResponse<GetAvailabilityResponse> | undefined> => {
  try {
    const response = await apiServiceHandler.get<APIResponse<GetAvailabilityResponse>>(GetAvailabilityURL, payload);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('GetAvailability error:', error);
    throw error;
  }
};

export const CreateBooking = async (payload: CreateBookingPayload): Promise<APIResponse<CreateBookingResponse> | undefined> => {
  try {
    const response = await apiServiceHandler.post<APIResponse<CreateBookingResponse>>(CreateBookingURL, payload);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('CreateBooking error:', error);
    throw error;
  }
};

export const CreateBulkBooking = async (payload: CreateBulkBookingPayload): Promise<APIResponse<CreateBulkBookingResponse> | undefined> => {
  try {
    const response = await apiServiceHandler.post<APIResponse<CreateBulkBookingResponse>>(CreateBulkBookingURL, payload);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('CreateBulkBooking error:', error);
    throw error;
  }
};

export const CancelBooking = async (bookingId: string, payload: CancelBookingPayload): Promise<APIResponse<CancelBookingResponse> | undefined> => {
  try {
    const url = `${CancelBookingURL}/${bookingId}`;
    const response = await apiServiceHandler.put<APIResponse<CancelBookingResponse>>(url, payload);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('CancelBooking error:', error);
    throw error;
  }
};

export const CancelBulkBooking = async (bulkBookingId: string): Promise<APIResponse<CancelBulkBookingResponse> | undefined> => {
  try {
    const url = `${CancelBulkBookingURL}/${bulkBookingId}`;
    const response = await apiServiceHandler.delete<APIResponse<CancelBulkBookingResponse>>(url);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('CancelBulkBooking error:', error);
    throw error;
  }
};

export const UpdateBooking = async (bookingId: string, payload: UpdateBookingPayload): Promise<APIResponse<UpdateBookingResponse> | undefined> => {
  try {
    const url = `${UpdateBookingURL}/${bookingId}`;
    const response = await apiServiceHandler.put<APIResponse<UpdateBookingResponse>>(url, payload);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('UpdateBooking error:', error);
    throw error;
  }
};

export const GetBookingStats = async (payload: GetStatsPayload): Promise<APIResponse<GetStatsResponse> | undefined> => {
  try {
    const url = `${GetStatsURL}/${payload.username}`;
    const params: any = {};
    if (payload.startDate) params.startDate = payload.startDate;
    if (payload.endDate) params.endDate = payload.endDate;

    const response = await apiServiceHandler.get<APIResponse<GetStatsResponse>>(url, params);
    return response === null ? undefined : response;
  } catch (error) {
    console.error('GetBookingStats error:', error);
    throw error;
  }
};
